// frontend/src/components/BlockchainDashboard.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useWeb3 } from "../hooks/useWeb3";
import { useEntropyOracle } from "../hooks/useEntropyOracle";
import { useEntropyToken } from "../hooks/useEntropyToken";
import { useEntropyNFT } from "../hooks/useEntropyNFT";

export default function BlockchainDashboard() {
  const web3 = useWeb3();
  const entropyOracle = useEntropyOracle();
  const entropyToken = useEntropyToken();
  const entropyNFT = useEntropyNFT();

  const [tokenInfo, setTokenInfo] = useState({ name: "", symbol: "", totalSupply: "0" });
  const [nftInfo, setNftInfo] = useState({ name: "", symbol: "", totalSupply: 0 });
  const [userBalance, setUserBalance] = useState("0");
  const [userNFTBalance, setUserNFTBalance] = useState(0);
  const [currentEntropy, setCurrentEntropy] = useState(null);
  const [entropyHistory, setEntropyHistory] = useState([]);

  const loadTokenInfo = useCallback(async () => {
    try {
      const [name, symbol, totalSupply] = await Promise.all([
        entropyToken.getName(),
        entropyToken.getSymbol(),
        entropyToken.getTotalSupply()
      ]);
      setTokenInfo({ name, symbol, totalSupply });
    } catch (err) {
      console.error("Erreur chargement token info:", err);
    }
  }, [entropyToken]);

  const loadNFTInfo = useCallback(async () => {
    try {
      const [name, symbol, totalSupply] = await Promise.all([
        entropyNFT.getName(),
        entropyNFT.getSymbol(),
        entropyNFT.getTotalSupply()
      ]);
      setNftInfo({ name, symbol, totalSupply });
    } catch (err) {
      console.error("Erreur chargement NFT info:", err);
    }
  }, [entropyNFT]);

  const loadUserBalance = useCallback(async () => {
    try {
      const balance = await entropyToken.getBalance(web3.address);
      setUserBalance(balance);
    } catch (err) {
      console.error("Erreur chargement user balance:", err);
    }
  }, [web3.address, entropyToken]);

  const loadUserNFTBalance = useCallback(async () => {
    try {
      const balance = await entropyNFT.getBalance(web3.address);
      setUserNFTBalance(balance);
    } catch (err) {
      console.error("Erreur chargement user NFT balance:", err);
    }
  }, [web3.address, entropyNFT]);

  const loadEntropyData = useCallback(async () => {
    try {
      const [current, history] = await Promise.all([
        entropyOracle.getCurrentEntropy(),
        entropyOracle.getEntropyHistory(10)
      ]);
      setCurrentEntropy(current);
      setEntropyHistory(history);
    } catch (err) {
      console.error("Erreur chargement entropy data:", err);
    }
  }, [entropyOracle]);

  // Charge les informations au montage
  useEffect(() => {
    if (!entropyToken.isLoading && entropyToken.contract) {
      loadTokenInfo();
    }
  }, [entropyToken.isLoading, entropyToken.contract, loadTokenInfo]);

  useEffect(() => {
    if (!entropyNFT.isLoading && entropyNFT.contract) {
      loadNFTInfo();
    }
  }, [entropyNFT.isLoading, entropyNFT.contract, loadNFTInfo]);

  useEffect(() => {
    if (!entropyOracle.isLoading && entropyOracle.contract) {
      loadEntropyData();
    }
  }, [entropyOracle.isLoading, entropyOracle.contract, loadEntropyData]);

  useEffect(() => {
    if (web3.address && entropyToken.contract) {
      loadUserBalance();
    }
  }, [web3.address, entropyToken.contract, loadUserBalance]);

  useEffect(() => {
    if (web3.address && entropyNFT.contract) {
      loadUserNFTBalance();
    }
  }, [web3.address, entropyNFT.contract, loadUserNFTBalance]);

  if (web3.isLoading || entropyOracle.isLoading || entropyToken.isLoading || entropyNFT.isLoading) {
    return (
      <div className="p-6 text-white">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="h-48 bg-gray-700 rounded mb-4"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 text-white max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-pink-600 bg-clip-text text-transparent">
        🔷 Blockchain Dashboard
      </h1>

      {/* Section Réseau */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-900 rounded-xl p-4 border border-gray-700">
          <h3 className="text-sm text-gray-400 mb-2">Réseau</h3>
          <p className="text-2xl font-bold">{web3.network?.name || "Hardhat"}</p>
          <p className="text-sm text-gray-500">Chain ID: {web3.network?.chainId || "31337"}</p>
        </div>

        <div className="bg-gray-900 rounded-xl p-4 border border-gray-700">
          <h3 className="text-sm text-gray-400 mb-2">Bloc Actuel</h3>
          <p className="text-2xl font-bold">{web3.network?.blockNumber || "---"}</p>
          <p className="text-sm text-gray-500">Dernière mise à jour</p>
        </div>

        <div className="bg-gray-900 rounded-xl p-4 border border-gray-700">
          <h3 className="text-sm text-gray-400 mb-2">Wallet</h3>
          {web3.isConnected ? (
            <>
              <p className="text-sm font-mono truncate">{web3.address}</p>
              <p className="text-lg font-bold text-green-400">{parseFloat(web3.balance).toFixed(4)} ETH</p>
            </>
          ) : (
            <button
              onClick={web3.connectWallet}
              className="bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg transition-colors"
            >
              Connecter Wallet
            </button>
          )}
        </div>
      </div>

      {/* Section Contrats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* EntropyOracle */}
        <div className="bg-gradient-to-br from-purple-900/50 to-gray-900 rounded-xl p-6 border border-purple-500/30">
          <h2 className="text-xl font-bold mb-4 text-purple-400">⚡ EntropyOracle</h2>
          <div className="space-y-2 text-sm">
            <p className="text-gray-400">Adresse:</p>
            <p className="font-mono text-xs break-all bg-black/30 p-2 rounded">{entropyOracle.address}</p>
            
            {currentEntropy && (
              <>
                <p className="text-gray-400 mt-4">Entropie Actuelle:</p>
                <p className="text-2xl font-bold text-purple-300">{currentEntropy.value.slice(0, 16)}...</p>
                <p className="text-xs text-gray-500">
                  {new Date(currentEntropy.timestamp * 1000).toLocaleString()}
                </p>
              </>
            )}
          </div>
        </div>

        {/* EntropyToken */}
        <div className="bg-gradient-to-br from-blue-900/50 to-gray-900 rounded-xl p-6 border border-blue-500/30">
          <h2 className="text-xl font-bold mb-4 text-blue-400">💎 {tokenInfo.symbol}</h2>
          <div className="space-y-2 text-sm">
            <p className="text-gray-400">Adresse:</p>
            <p className="font-mono text-xs break-all bg-black/30 p-2 rounded">{entropyToken.address}</p>
            
            <p className="text-gray-400 mt-4">Nom:</p>
            <p className="font-semibold">{tokenInfo.name}</p>
            
            <p className="text-gray-400">Total Supply:</p>
            <p className="text-2xl font-bold text-blue-300">{parseFloat(tokenInfo.totalSupply).toFixed(2)}</p>
            
            {web3.isConnected && (
              <>
                <p className="text-gray-400 mt-4">Votre Solde:</p>
                <p className="text-xl font-bold text-green-400">{parseFloat(userBalance).toFixed(4)} {tokenInfo.symbol}</p>
              </>
            )}
          </div>
        </div>

        {/* EntropyNFT */}
        <div className="bg-gradient-to-br from-pink-900/50 to-gray-900 rounded-xl p-6 border border-pink-500/30">
          <h2 className="text-xl font-bold mb-4 text-pink-400">🎨 {nftInfo.symbol}</h2>
          <div className="space-y-2 text-sm">
            <p className="text-gray-400">Adresse:</p>
            <p className="font-mono text-xs break-all bg-black/30 p-2 rounded">{entropyNFT.address}</p>
            
            <p className="text-gray-400 mt-4">Collection:</p>
            <p className="font-semibold">{nftInfo.name}</p>
            
            <p className="text-gray-400">Total Minted:</p>
            <p className="text-2xl font-bold text-pink-300">{nftInfo.totalSupply}</p>
            
            {web3.isConnected && (
              <>
                <p className="text-gray-400 mt-4">Vos NFTs:</p>
                <p className="text-xl font-bold text-green-400">{userNFTBalance}</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Historique Entropy */}
      {entropyHistory.length > 0 && (
        <div className="bg-gray-900 rounded-xl p-6 border border-gray-700">
          <h2 className="text-xl font-bold mb-4">📊 Historique Entropie</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 border-b border-gray-700">
                  <th className="text-left py-2">Valeur</th>
                  <th className="text-left py-2">Source</th>
                  <th className="text-left py-2">Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {entropyHistory.map((entry, index) => (
                  <tr key={index} className="border-b border-gray-800">
                    <td className="py-2 font-mono text-xs">{entry.value.slice(0, 20)}...</td>
                    <td className="py-2">{entry.source}</td>
                    <td className="py-2 text-gray-400">
                      {new Date(entry.timestamp * 1000).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
